/**
 * Cosmos Homepage v2 — wired App.tsx for the new public-facing homepage.
 *
 * The previous landing-page App.tsx is preserved in App.original.tsx so we can
 * revert by swapping the import or restoring the file.
 *
 * Rendering map (top → bottom):
 *   NewNavBar
 *   HeroSection            (dynamic stats from /api/v1/homepage/stats, fallback baked-in;
 *                           "+N NEW!" over a rolling window, set in Admin → Homepage)
 *   SearchSection          (Typesense keyword search via /home/search; bubbles + Surprise Me)
 *   SearchResults          (teaser cards for logged-out, locked filters)
 *   UniversityTicker       (every university the admin panel gave a website label, from
 *                           the API's homepage doc; a hardcoded 13 only as the fallback)
 *   TrustedBy              ("Trusted by R&D Leaders at" logo banner)
 *   SuccessStories         (matchmaking rows; was CosmicConnections) — both from
 *                           /api/v1/homepage/content (Admin → Homepage), built-in
 *                           copy until it answers or when it cannot
 *   RoadmapSection         (3 pillars; OCEAN CTAs open EarlyAccessModal)
 *   Testimonials           (real quotes from About Us page)
 *   DiscoverNext           (rotating word + List Your IP modal)
 *   NewFooter              (cosmos newsletter signup + working links + LinkedIn)
 *
 * Auth-gated UX is logged-out throughout (this file is only rendered when no
 * Cosmos session is present). Any "locked" interaction opens the existing
 * LoginModal / SignUpModal so the rest of the auth flow is unchanged.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { usePageTitle } from '../utils/pageTitle';
import LoginModal from './components/LoginModal';
import SignUpModal from './components/SignUpModal';
import RequestInviteModal from './components/RequestInviteModal';
import SupportModal from './components/SupportModal';
import { PrivacyPolicyModal } from './components/PrivacyPolicyModal';
import { TermsOfServiceModal } from './components/TermsOfServiceModal';
import { WhitePaperModal } from './components/WhitePaperModal';
import { OAuthSignupProgressOverlay } from '../components/OAuthSignupProgressOverlay';
import { SignupRequiredModal } from '../components/SignupRequiredModal';

import {
  NewNavBar, NavItem,
  HeroSection, HeroStats,
  SearchSection,
  SearchResults,
  UniversityTicker, UniversityTickerItem,
  TrustedBy, SuccessStories,
  RoadmapSection,
  Testimonials,
  DiscoverNext, ListYourIPModal,
  EarlyAccessModal, EarlyAccessSource,
  NewFooter,
} from './components/NewHome';

import { apiService, SESSION_EXPIRED_EVENT, SESSION_EXPIRED_FLAG } from '../services/apiService';
import type { HomeSearchSource, HomepageContent } from '../services/apiService';
import { trackClick } from '../utils/analytics';
import { currentIds } from '../utils/visitorTracking';
import { clearReturnAfterAuth, NEXT_PARAM } from '../utils/returnAfterAuth';

interface LandingAppProps {
  onLogin: (email: string) => void;
  onNavigateToAbout?: () => void;
}

// --- Static fallbacks used until backend stat / ticker endpoints are live ---

// Last-resort values for when the API can't be reached at all. Deltas are 0
// so we never advertise growth we haven't verified — StatBlock hides a zero
// badge, which is the honest thing to show when we don't actually know.
// Refresh these whenever the corpus grows substantially; they are only ever
// visible on a failed fetch.
const FALLBACK_STATS: HeroStats = {
  universities: 37,
  universities_new_this_month: 0,
  tech_offers: 27743,
  tech_offers_new_this_month: 0,
  countries: 6,
  countries_new_this_month: 0,
};

// ---------------------------------------------------------------------------
// FALLBACK ONLY. Since 2026-09 the ticker is driven by the admin panel's
// website labels (Database → Universities → "UTECH Website Label"): every
// university with a label is on the ticker under that label, and one without
// a label is not on it. The API's homepage doc carries those labels, so this
// list is used only when that doc cannot be fetched at all or is an older
// build with no labels in it.
//
// The list is the original pinned 13 (per the "University Shortforms for
// homepage display" spreadsheet) and keeps its intentional order for that
// case. `analyticsKey` is the canonical full name to look up in the analytics
// doc for the count + delta-vs-prev calculation.
// ---------------------------------------------------------------------------
interface PinnedUniversity {
  shortName: string;
  country: string;
  analyticsKey?: string;   // full name as it appears in analytics_pie.json
  /**
   * Hardcoded count to display when the university isn't in our analytics
   * snapshots (Monash / SRMIST / Melbourne). Drop this when the snapshot
   * starts including the university.
   */
  fallbackCount?: number;
}

const PINNED_UNIVERSITIES: PinnedUniversity[] = [
  { shortName: 'MIT', country: 'United States', analyticsKey: 'Massachusetts Institute of Technology' },
  { shortName: 'Oxford', country: 'United Kingdom', analyticsKey: 'Oxford University' },
  { shortName: 'IIT Bombay', country: 'India', analyticsKey: 'Indian Institute of Technology Bombay' },
  { shortName: 'IISc', country: 'India', analyticsKey: 'Indian Institute of Science' },
  { shortName: 'RWTH Aachen', country: 'Germany', analyticsKey: 'RWTH Aachen University' },
  { shortName: 'Stanford', country: 'United States', analyticsKey: 'Stanford University' },
  { shortName: 'Monash', country: 'Australia', analyticsKey: 'Monash University' },
  { shortName: 'Toronto', country: 'Canada', analyticsKey: 'University of Toronto' },
  { shortName: 'SRMIST', country: 'India', analyticsKey: 'SRM Institute of Science & Technology (SRMIST)' },
  { shortName: 'Durham', country: 'United Kingdom', analyticsKey: 'Durham University' },
  { shortName: 'KIT', country: 'Germany', analyticsKey: 'Karlsruhe Institute of Technology (KIT)' },
  { shortName: 'CSIR', country: 'India', analyticsKey: 'Council of Scientific & Industrial Research (CSIR)' },
  { shortName: 'Melbourne', country: 'Australia', analyticsKey: 'The University of Melbourne' },
];

const FALLBACK_TICKER: UniversityTickerItem[] = PINNED_UNIVERSITIES.map(p => ({
  name: p.shortName,
  country: p.country,
  count: p.fallbackCount ?? 0,
}));

// ---------------------------------------------------------------------------
// Loading the university list for the ticker
// ---------------------------------------------------------------------------
//
// We have month-stamped analytics snapshots in /public:
//   analytics_pie.json     ← latest (counts shown today)
//   analytics_pie_mar.json ← previous month (used to compute "+N NEW!" delta)
//
// Source-of-truth strategy (per product decision):
//   1. Fetch the API's homepage doc (current totals + admin website labels)
//   2. Fetch /analytics_pie_mar.json (prior totals) for the delta
//   3. Ticker = every labelled university, shown under its label
//   4. Fall back silently to the pinned list when the doc carries no labels

interface AnalyticsPieUniversity {
  name: string;
  country: string;
  technologies: number;
  researchers: number;
  /** The admin panel's website label ("MIT", "IIT Bombay") — the name the
   *  ticker shows. Only the API's homepage doc carries it (since 2026-09);
   *  the frozen monthly snapshot and older docs have neither field. */
  label?: string | null;
  /** Server-side "is on the ticker" — true exactly when a label is set. */
  featured?: boolean;
}

interface AnalyticsPiePayload {
  metadata?: {
    generated_at?: string;
    total_technologies?: number;
    total_universities?: number;
  };
  universities?: AnalyticsPieUniversity[];
  country_distribution?: Record<string, unknown>;
}

const fetchAnalyticsPie = async (path: string): Promise<AnalyticsPiePayload | null> => {
  try {
    // `no-cache` revalidates with the server every load, so we always pick up
    // a freshly-published analytics_pie.json (e.g. when product adds new
    // universities to the snapshot). Used to be `force-cache`, which silently
    // pinned the page to whatever the browser had cached and made new
    // entries invisible until a hard refresh.
    const r = await fetch(path, { cache: 'no-cache' });
    if (!r.ok) return null;
    return (await r.json()) as AnalyticsPiePayload;
  } catch {
    return null;
  }
};

/**
 * Homepage analytics, served by the API from a file it rebuilds at startup
 * (GET /api/v1/homepage/analytics). This replaces the hand-published
 * /public/analytics_pie.json, which had to be regenerated and redeployed by
 * hand — so a university added to the database stayed invisible on the
 * homepage until someone remembered to do that.
 *
 * Caching: the payload carries a content `version`. We keep the last copy in
 * localStorage and send the version we hold; when it still matches, the server
 * answers 304 with no body and we render straight from cache. A fresh app load
 * therefore costs one small request, and only a genuinely changed corpus costs
 * the full document.
 */
const ANALYTICS_CACHE_KEY = 'utech_homepage_analytics_v1';

type CachedAnalytics = { version: string; payload: AnalyticsPiePayload };

const readAnalyticsCache = (): CachedAnalytics | null => {
  try {
    const raw = localStorage.getItem(ANALYTICS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedAnalytics;
    return parsed?.version && parsed?.payload ? parsed : null;
  } catch {
    return null;
  }
};

const fetchHomepageAnalytics = async (): Promise<AnalyticsPiePayload | null> => {
  const cached = readAnalyticsCache();
  try {
    // Same origin the rest of the app talks to. Resolving this independently
    // (e.g. from import.meta.env) would fall back to the page's own origin in
    // production, where the API lives on a different host.
    const base = apiService.getApiBaseUrl?.() || '';
    const url = `${base}/api/v1/homepage/analytics`
      + (cached?.version ? `?version=${encodeURIComponent(cached.version)}` : '');
    const r = await fetch(url, { cache: 'no-store' });

    // 304: what we already hold is current.
    if (r.status === 304 && cached) return cached.payload;
    if (!r.ok) return cached?.payload ?? null;

    const payload = (await r.json()) as AnalyticsPiePayload & { version?: string };
    if (payload?.version) {
      try {
        localStorage.setItem(ANALYTICS_CACHE_KEY,
          JSON.stringify({ version: payload.version, payload }));
      } catch { /* private mode / quota — rendering does not depend on the cache */ }
    }
    return payload;
  } catch {
    // Offline or the API is down: the last good copy beats an empty homepage.
    return cached?.payload ?? null;
  }
};

/**
 * The admin-edited homepage blocks (GET /api/v1/homepage/content): the
 * "Trusted by" logo banner and the Success Stories rows. The last good copy
 * is kept in localStorage, so a returning visitor starts from the admin's
 * version — no swap from the built-in copy as the answer lands, and no
 * built-in banner flashing up when the admin has switched it off.
 */
const CONTENT_CACHE_KEY = 'utech_homepage_content_v1';

const readContentCache = (): HomepageContent | null => {
  try {
    const raw = localStorage.getItem(CONTENT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomepageContent;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/** The label the ticker would show for this entry, or '' when it is not on
 *  the ticker. A label is the whole criterion: the admin panel's rule is
 *  "give it a short name and it is featured; leave it blank and it is not".
 *  `featured` is the server saying the same thing, so an explicit false is
 *  honoured should the two ever be decoupled. */
const tickerLabel = (u: AnalyticsPieUniversity): string => {
  const label = (u.label || '').trim();
  return label && u.featured !== false ? label : '';
};

/**
 * Build the homepage ticker from the API's homepage doc: every university
 * the admin panel gave a website label, shown under that label, biggest
 * corpus first. Count is the live technologies figure; the "+N NEW!" delta
 * is the difference against the frozen monthly snapshot, matched by the
 * university's full name (the snapshot predates labels).
 *
 * Until 2026-09 this rendered a hardcoded list of 13 whatever the admin
 * panel said, which is why labels set there "did nothing". That list is now
 * the fallback for a doc with no labels at all — an older API build, or the
 * doc failing to load — so the homepage never shows an empty ticker. For
 * universities the snapshot does not know (Monash, SRMIST, Melbourne) the
 * fallback path keeps its hardcoded `fallbackCount` so it never shows
 * "0 technologies" next to a real school.
 */
const buildTickerFromAnalytics = (
  current: AnalyticsPiePayload | null,
  previous: AnalyticsPiePayload | null,
): UniversityTickerItem[] => {
  const curByName = new Map<string, number>();
  (current?.universities || []).forEach(u => curByName.set(u.name, u.technologies || 0));
  const prevByName = new Map<string, number>();
  (previous?.universities || []).forEach(u => prevByName.set(u.name, u.technologies || 0));

  const labelled = (current?.universities || []).filter(u => tickerLabel(u));
  if (labelled.length > 0) {
    return [...labelled]
      .sort((a, b) => (b.technologies || 0) - (a.technologies || 0))
      .map(u => {
        const cur = u.technologies || 0;
        const prev = prevByName.get(u.name);
        // No delta for a university the snapshot has never seen: "+2,000
        // NEW!" for a school that was simply added since would be untrue.
        const delta = prev !== undefined ? Math.max(0, cur - prev) : 0;
        return {
          name: tickerLabel(u),
          country: u.country || '',
          count: cur,
          delta: delta > 0 ? delta : undefined,
        };
      });
  }

  return PINNED_UNIVERSITIES.map(p => {
    const curFromAnalytics = p.analyticsKey ? curByName.get(p.analyticsKey) : undefined;
    const prevFromAnalytics = p.analyticsKey ? prevByName.get(p.analyticsKey) : undefined;
    const cur = curFromAnalytics ?? p.fallbackCount ?? 0;
    const prev = prevFromAnalytics ?? p.fallbackCount ?? 0;
    // Delta only computed for analytics-backed entries; fallback rows have
    // no meaningful "+N NEW" because both numbers are the same hardcoded value.
    const delta = curFromAnalytics !== undefined && prevFromAnalytics !== undefined
      ? Math.max(0, curFromAnalytics - prevFromAnalytics)
      : 0;
    return {
      name: p.shortName,
      country: p.country,
      count: cur,
      delta: delta > 0 ? delta : undefined,
    };
  });
};

const App: React.FC<LandingAppProps> = ({ onLogin, onNavigateToAbout }) => {
  usePageTitle('Home');
  // ---- Auth modals -------------------------------------------------------
  const [showLoginModal, setShowLoginModal] = useState(false);
  // The sign-in ran out in the app (index.tsx SessionExpiredHandler): open the
  // sign-in modal and say why. The flag goes when the modal closes or they
  // sign in, so a reload in between (Clerk's sign-out) opens it again.
  const [sessionExpired, setSessionExpired] = useState(() => {
    try { return sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1'; } catch { return false; }
  });
  useEffect(() => { if (sessionExpired) setShowLoginModal(true); }, [sessionExpired]);
  useEffect(() => {
    const onExpired = () => setSessionExpired(true);   // already on screen when it ran out
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);
  const clearSessionExpired = () => {
    try { sessionStorage.removeItem(SESSION_EXPIRED_FLAG); } catch { /* private mode */ }
    setSessionExpired(false);
  };
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showRequestInviteModal, setShowRequestInviteModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState(false);
  const [showSignupRequiredModal, setShowSignupRequiredModal] = useState(false);
  const [showWhitePaperModal, setShowWhitePaperModal] = useState(false);
  const [showOAuthSignupOverlay] = useState(false);
  const [showListYourIPModal, setShowListYourIPModal] = useState(false);
  const [earlyAccessSource, setEarlyAccessSource] = useState<EarlyAccessSource | null>(null);
  const [signupSource, setSignupSource] = useState<string | null>(null);

  // ---- Search state ------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  // The search the cards on screen came from: the server's log row id and the
  // query it ran. Every card / View More / locked-filter click carries both,
  // which is how the admin log ties "clicked Read More #2" to a search.
  const [searchMeta, setSearchMeta] = useState<{ search_id: string | null; query: string }>(
    { search_id: null, query: '' });
  // Last search wins: a slow answer to an earlier query must not replace the
  // cards (and the search_id) of a later one.
  const searchSeq = useRef(0);

  // ---- Stats + ticker + content (with graceful fallback) -----------------
  const [stats, setStats] = useState<HeroStats>(FALLBACK_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [ticker, setTicker] = useState<UniversityTickerItem[]>(FALLBACK_TICKER);
  // null = the components' built-in copy (first visit, until the API answers).
  const [content, setContent] = useState<HomepageContent | null>(readContentCache);

  // Refs for in-page scroll
  const searchSectionRef = useRef<HTMLDivElement | null>(null);

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  // Fetch homepage stats (live count + "+N NEW!" delta) from the API, which
  // serves them from a file it rebuilds off the live corpus at startup, with
  // Admin → Homepage's choices applied (a typed number, the delta's rolling
  // window — last 30 days by default — or the line switched off).
  // Falls back silently to FALLBACK_STATS if the call fails.
  //
  // The response is authoritative for every field, deltas included. It used to
  // treat a `0` delta as "no value" and substitute whatever the fallback
  // constant held, which is how the homepage came to advertise "+1 NEW!"
  // country when no country had been added.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await (apiService as any).getHomepageStats?.();
        if (cancelled) return;
        if (res?.success && res.data?.stats) {
          const incoming = res.data.stats as Partial<HeroStats>;
          setStats({ ...FALLBACK_STATS, ...incoming });
        }
      } catch { /* keep fallback */ }
      finally { if (!cancelled) setStatsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // The "Trusted by" banner + Success Stories content. Best effort, once per
  // load: TrustedBy / SuccessStories render the cached copy or their built-in
  // one until this answers, and keep it if the call fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getHomepageContent();
        if (cancelled) return;
        const fresh = res.success && res.data?.success ? res.data.content : null;
        if (fresh) {
          setContent(fresh);
          try {
            localStorage.setItem(CONTENT_CACHE_KEY, JSON.stringify(fresh));
          } catch { /* private mode / quota — rendering does not depend on the cache */ }
        }
      } catch { /* keep what is shown */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the university ticker. Which universities are on it, and under
  // what name, comes from the admin website labels the API's homepage doc
  // carries; the per-university count is the live corpus and the "+N NEW!"
  // delta is a diff against the frozen monthly snapshot in /public.
  // Hero-stat deltas are NOT computed here — the API owns those.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // `current` now comes from the API's generated file, so the ticker and
      // the hero counts reflect the live corpus. The monthly snapshot stays a
      // static file: it is a frozen point in history used only to compute the
      // "+N NEW!" delta, and there is nothing to regenerate it from.
      const [current, previous] = await Promise.all([
        fetchHomepageAnalytics(),
        fetchAnalyticsPie('/analytics_pie_mar.json'),
      ]);
      if (cancelled) return;
      const items = buildTickerFromAnalytics(current, previous);
      if (items.length > 0) setTicker(items);

    })();
    return () => { cancelled = true; };
  }, []);

  // -----------------------------------------------------------------------
  // Search
  // -----------------------------------------------------------------------

  const runSearch = async (q: string, source: HomeSearchSource = 'typed') => {
    const trimmed = (q || '').trim();
    const mine = ++searchSeq.current;
    if (!trimmed) {
      setSearchResults([]); setSearchTotal(0);
      setSearchMeta({ search_id: null, query: '' });
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      // /home/search: Typesense (the server falls back to Algolia on its own)
      // and logged server-side with this visit's ids, so the admin panel's
      // Homepage searches tab has every anonymous search. Teaser cards are
      // capped at 3 — anything beyond requires sign-up (Section 4 of the spec).
      let res = await apiService.homeSearch(trimmed, source, currentIds());
      if (mine !== searchSeq.current) return;
      // An API without /home/search yet (404), or one that could not answer:
      // the homepage's previous direct Algolia call, so the teaser never goes
      // blank. Not on 429 — that visitor is being slowed down on purpose.
      if (!res.success && res.error !== 'rate_limited') {
        const old = await apiService.directSearch('algolia', { query: trimmed, filters: {}, page: 0, hits_per_page: 3 });
        if (mine !== searchSeq.current) return;
        if (old.success && old.data) {
          res = { success: true, data: { results: old.data.results || [], total: old.data.total || 0,
                                         engine: 'algolia-direct', took_ms: 0, search_id: null } };
        }
      }
      if (res.success && res.data) {
        const d = res.data;
        const total = d.total || 0;
        setSearchResults((d.results || []).slice(0, 3));
        setSearchTotal(total);
        setSearchMeta({ search_id: d.search_id ?? null, query: trimmed });
        trackClick('search_results_shown', {
          search_id: d.search_id ?? null, query: trimmed, total, engine: d.engine, source,
        });
        // Smooth-scroll the results into view on small screens.
        setTimeout(() => searchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      } else {
        setSearchResults([]); setSearchTotal(0);
        setSearchMeta({ search_id: null, query: trimmed });
      }
    } catch {
      if (mine !== searchSeq.current) return;
      setSearchResults([]); setSearchTotal(0);
      setSearchMeta({ search_id: null, query: trimmed });
    } finally {
      if (mine === searchSeq.current) setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    searchSeq.current++;            // drop any answer still in flight
    setSearchQuery('');
    setSearchResults([]);
    setSearchTotal(0);
    setSearchMeta({ search_id: null, query: '' });
    setSearchLoading(false);
  };

  // -----------------------------------------------------------------------
  // Auth modal handlers (preserved from original App.tsx so the existing
  // login / signup / clerk flow still works unchanged).
  // -----------------------------------------------------------------------

  const closeAllModals = () => {
    setShowLoginModal(false); setShowSignUpModal(false);
    setShowRequestInviteModal(false); setShowSupportModal(false);
    setShowPrivacyPolicyModal(false);
    setShowTermsOfServiceModal(false); setShowSignupRequiredModal(false);
    setShowListYourIPModal(false); setShowWhitePaperModal(false);
    setEarlyAccessSource(null);
  };

  /** A sign-in started HERE is not a return to a tech page: forget any
   *  pending one (utils/returnAfterAuth) and its ?next= in the address. */
  const forgetReturn = () => {
    clearReturnAfterAuth();
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has(NEXT_PARAM)) {
        url.searchParams.delete(NEXT_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch { /* ignore */ }
  };

  // Back from Google / LinkedIn sign-UP (/sso-callback → "/"): the sign-up
  // modal finishes it (creates the account from the Clerk profile, then
  // reloads signed in), as the legacy landing did — without it the visitor
  // sat on the signed-out home, the Clerk sign-up never turned into an
  // account, and a tech page's return was lost.
  // Only with a live Clerk session (the Google / LinkedIn sign-up really
  // happened): a stale flag on its own does not pop the modal open.
  const { session: clerkSession, isLoaded: clerkLoaded } = useSession();
  const [resumingOAuthSignup, setResumingOAuthSignup] = useState(false);
  useEffect(() => {
    if (!clerkLoaded || !clerkSession || resumingOAuthSignup) return;
    try {
      if (sessionStorage.getItem('clerk_signup_mode') !== 'true') return;
      if (apiService.getStoredUserId() && apiService.getStoredAccessToken()) return;
      setResumingOAuthSignup(true);
      setShowLoginModal(false);
      setShowSignUpModal(true);
    } catch { /* storage off */ }
  }, [clerkLoaded, clerkSession, resumingOAuthSignup]);

  const openLogin = () => { trackClick('open_login'); forgetReturn(); closeAllModals(); setShowLoginModal(true); };
  const openSignUp = (source?: string | null) => {
    trackClick('open_signup', { source: source || undefined });
    forgetReturn();
    setResumingOAuthSignup(false);
    setSignupSource(source || null);
    // Stamp the URL with utm_source so PostHog and any downstream auth
    // callback can attribute the signup to the specific CTA the user came
    // from (e.g. ocean_search vs ocean_blue vs list_your_ip). Using
    // replaceState avoids polluting browser history.
    if (source) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('utm_source', source);
        url.searchParams.set('utm_medium', 'cta');
        url.searchParams.set('utm_campaign', 'cosmos_v2_signup');
        window.history.replaceState({}, '', url.toString());
      } catch { /* swallow malformed URL edge cases */ }
    }
    closeAllModals();
    setShowSignUpModal(true);
  };
  const openSignUpFromCard = () => {
    setShowSignupRequiredModal(true);
  };
  const handleSignUpSuccess = () => {
    setShowSignUpModal(false);
    setShowLoginModal(true);
  };
  const openSupport = () => { closeAllModals(); setShowSupportModal(true); };
  const openListYourIP = () => { closeAllModals(); setShowListYourIPModal(true); };
  const openEarlyAccess = (source: EarlyAccessSource) => {
    // "Sign Up for Early Access" CTAs now route into the full platform
    // signup flow (per product), tagging the lead with a UTM source so the
    // backend / PostHog see "they came from ocean_search" / "ocean_blue".
    // The lightweight EarlyAccessModal stays in code for future use but is
    // no longer triggered by these buttons.
    trackClick('early_access_open', { source });
    openSignUp(source);
  };

  const handleAboutUs = () => {
    trackClick('nav_about_us');
    // The Cosmos v2 About Us is now the canonical About Us on /about.
    window.location.href = '/about';
  };

  // -----------------------------------------------------------------------
  // Nav config — adding a new top-bar item is a one-line change here.
  // -----------------------------------------------------------------------

  // "What's New" is intentionally hidden until the page exists. Restore by
  // adding `{ id: 'whats_new', label: "What's New", href: '/whats-new' }`.
  const navItems: NavItem[] = useMemo(() => [
    { id: 'about_us', label: 'About Us', href: '#', onClick: handleAboutUs },
  ], []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="bg-white min-h-screen font-dm-sans text-grayscale-1200 overflow-x-hidden">
      {showOAuthSignupOverlay && <OAuthSignupProgressOverlay />}
      {showLoginModal && (
        <LoginModal onClose={() => { clearSessionExpired(); closeAllModals(); }}
          onLogin={email => { clearSessionExpired(); onLogin(email); }}
          onSignUpClick={() => { clearSessionExpired(); openSignUp(signupSource); }}
          {...(sessionExpired ? { heading: 'Your session has expired', subheading: 'Please sign in again to continue.' } : {})} />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={() => { setResumingOAuthSignup(false); closeAllModals(); }}
          onSignupSuccess={handleSignUpSuccess}
          // Finishing a Google / LinkedIn sign-up: keep a tech page's return
          // (armed when that sign-up started there).
          returnAfterAuth={resumingOAuthSignup}
        />
      )}
      {showRequestInviteModal && <RequestInviteModal onClose={closeAllModals} />}
      {showSupportModal && <SupportModal onClose={closeAllModals} />}
      {showPrivacyPolicyModal && <PrivacyPolicyModal isOpen={showPrivacyPolicyModal} onClose={closeAllModals} />}
      {showTermsOfServiceModal && <TermsOfServiceModal isOpen={showTermsOfServiceModal} onClose={closeAllModals} />}
      <WhitePaperModal
        isOpen={showWhitePaperModal}
        onClose={() => setShowWhitePaperModal(false)}
        formSource="home_footer_whitepaper_modal"
      />
      {showSignupRequiredModal && (
        <SignupRequiredModal
          isOpen={showSignupRequiredModal}
          onClose={closeAllModals}
          onLogin={openLogin}
          onSignup={() => openSignUp(signupSource)}
        />
      )}

      {/* List Your IP modal lives in NewHome.tsx — wired through context-free props */}
      <ListYourIPModal open={showListYourIPModal} onClose={closeAllModals} />

      {/* Early access modal — used by OCEAN Blue / OCEAN Search CTAs */}
      <EarlyAccessModal
        open={!!earlyAccessSource}
        onClose={closeAllModals}
        source={earlyAccessSource}
      />

      {/* ---- Top nav ---- */}
      <NewNavBar
        navItems={navItems}
        onLoginClick={openLogin}
        onLogoClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      />

      {/* ---- Hero / search / results ---- */}
      <main>
        <div className="max-w-screen-xl mx-auto">
          <HeroSection stats={stats} loading={statsLoading} />
          {/* Chips + Surprise pool from Admin → Homepage → Search chips; the
              built-in ones until (or unless) the content call answers. */}
          <SearchSection
            query={searchQuery}
            setQuery={setSearchQuery}
            onSubmit={runSearch}
            bubbles={content?.search?.bubbles}
            surprisePool={content?.search?.surprise}
            surpriseLabel={content?.search?.surprise_label}
            hideBubbles={content?.search?.enabled === false}
          />
          <div ref={searchSectionRef}>
            <SearchResults
              query={searchQuery}
              loading={searchLoading}
              results={searchResults}
              total={searchTotal}
              onLockedClick={openSignUpFromCard}
              onClear={clearSearch}
              searchId={searchMeta.search_id}
              searchedQuery={searchMeta.query}
            />
          </div>
        </div>

        {/* University ticker — full-bleed visually. The "View all" CTA
            below the carousel hard-navigates to /universities so users can
            see the full directory rather than the rotating teaser. */}
        <UniversityTicker
          items={ticker}
          onViewAll={() => { trackClick('home_university_ticker_view_all'); window.location.href = '/universities'; }}
        />

        <div className="max-w-screen-xl mx-auto">
          <TrustedBy content={content?.trusted} />
          <SuccessStories content={content?.stories} />
          <RoadmapSection onSignUp={openSignUp} onEarlyAccess={openEarlyAccess} />
          <Testimonials />
          <DiscoverNext onSignUp={() => openSignUp()} onListYourIP={openListYourIP} />
        </div>
      </main>

      <NewFooter
        onShowAboutUs={handleAboutUs}
        onShowPrivacy={() => setShowPrivacyPolicyModal(true)}
        onShowTerms={() => setShowTermsOfServiceModal(true)}
        onShowRdSymposium={() => { trackClick('home_footer_rd_symposium'); window.location.href = '/rd-symposium'; }}
        onShowWhitePaper={() => { trackClick('home_footer_whitepaper'); setShowWhitePaperModal(true); }}
        onShowUniversities={() => { trackClick('home_footer_universities'); window.location.href = '/universities'; }}
        onShowComet={() => { trackClick('home_footer_comet'); window.location.href = '/comet'; }}
      />
    </div>
  );
};

export default App;
